import {
  Banner,
  Body,
  Box,
  Button,
  ContentRow,
  Dialog,
  Divider,
  fieldIssues,
  FieldProps,
  Heading,
  InfoIcon,
  InlineMessage,
  Loader,
  LocalizedString,
  NonNegative,
  NumberInputStepperField,
  Positive,
  PositiveInteger,
  Space,
  Stack,
  unsafeLocalizedString,
  unsafeNonNegative,
  unsafePositiveInteger,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import {
  array,
  boolean,
  either,
  nonEmptyArray,
  option,
  taskEither,
} from "fp-ts";
import {
  constant,
  constFalse,
  constNull,
  constTrue,
  constVoid,
  flow,
  identity,
  pipe,
} from "fp-ts/function";
import { Option } from "fp-ts/Option";
import { IO } from "fp-ts/IO";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  LocaleKey,
  RuntimeLocaleKey,
  useFormatCurrency,
  useFormatMessage,
  useFormatMoneyAmount,
  useFormatMoneyAmountParts,
  useFormatRichMessage2,
} from "../../intl";
import { CustomerOfferDetailsDialog } from "./CustomerOfferDetailsDialog";
import {
  Currency,
  ExistingClientAuthenticationMethod,
  foldCurrency,
  unsafeNonNegativeInteger,
} from "../../globalDomain";
import * as api from "../api";
import { Reader } from "fp-ts/Reader";
import { useCommand } from "../../useAPI";
import { CustomerOfferCard } from "./CustomerOfferCard";
import { LimitWarning, useOfferLimitsInformation } from "./LimitWarning";
import { StandardLoanFlowType } from "../domain";
import { YourOffer } from "./YourOffer";
import { SelectedLoansInformation } from "../Refinancing/RefinancingAccountSelection";
import { CPIDialog } from "./CPIDialog/CPIDialog";
import {
  ConfiguratorValues,
  foldAPIStatus,
  useUpdateCustomerOfferContext,
} from "./UpdateCustomerOfferContext";
import { CPIAdditionalQuestions, CPIPackageList } from "./api";
import { EntryFeeRadioGroup } from "./EntryFee";
import { TaskEither } from "fp-ts/TaskEither";
import { useAppContext } from "../../useAppContext";
import { useScrollTopOnChange } from "../../Common/useScrollTopOnChange";
import { InputSliderField } from "../../Common/InputSliderField/InputSliderField";

type Props = {
  flowType: StandardLoanFlowType;
  onNext: IO<unknown>;
  onExit: (isApplicationSaved: boolean) => unknown;
  loanOffer: api.GenericLoanResponseOutput;
  hasRefinancingCredits: boolean;
  allInternalLoanSelected: boolean;
  restoredAdditionalQuestions: Option<CPIAdditionalQuestions>;
  selectedRefinancingCredits: SelectedLoansInformation;
  authenticationMethod: Option<ExistingClientAuthenticationMethod>;
  onUpdateCustomerOffer: (status: api.UpdateOfferStatus) => void;
  cpiPackageList: Option<CPIPackageList>;
};

export type OfferDetails = {
  genericLoanOffer: api.GenericLoanOffer;
  refinancing: boolean;
};

interface ClampAndRoundParams {
  min: number;
  max: number;
  step: Positive;
}

function makeClampAndRound(
  params: ClampAndRoundParams
): Reader<number, number> {
  return amount => {
    // handle edge cases on max and min
    if (amount === params.min || amount === params.max) return amount;
    const rounded = Math.round(amount / params.step) * params.step;
    return Math.max(Math.min(rounded, params.max), params.min);
  };
}

function useDebounceAction<I>(
  action: Reader<I, void>,
  delay: number
): Reader<I, void> {
  const timeoutRef = useRef<Option<number>>(option.none);

  return (input: I) => {
    pipe(
      timeoutRef.current,
      option.fold(constVoid, currentTimeout => {
        window.clearTimeout(currentTimeout);
      })
    );

    timeoutRef.current = option.some(
      window.setTimeout(() => {
        action(input);
        timeoutRef.current = option.none;
      }, delay)
    );
  };
}

export function CustomerOfferConfigurator(props: Props) {
  useScrollTopOnChange("smooth");
  const {
    preapprovedLimitState,
    handleUpdateCustomerOffer,
    loanOfferState,
  } = useUpdateCustomerOfferContext();
  const {
    config: { enableBankFee50FE: enableBankFee50 },
  } = useAppContext();

  const [, updateState] = useState<unknown>();
  const forceUpdate = useCallback(() => updateState({}), []);
  const [bankingFeeValid, setBankingFeeValid] = useState(true);
  const isMobileLayout = useIsMobileLayout();

  const [offerDetails, setOfferDetails] = useState<Option<OfferDetails>>(
    option.none
  );

  const [moreInfo, setMoreInfo] = useState<boolean>(false);

  const [currentLoanOffer, setCurrentLoanOffer] = useState<
    api.GenericLoanResponseOutput & {
      isLoading: boolean;
    }
  >({
    ...props.loanOffer,
    isLoading: false,
  });

  //we should use this from BE/PCE
  const amountStep = option.isSome(currentLoanOffer.stepSizeAmount)
    ? currentLoanOffer.stepSizeAmount.value
    : pipe(
        currentLoanOffer.currency,
        foldCurrency({
          CZK: () => 5000,
          EUR: () => 200,
          USD: () => 200,
        }),
        unsafePositiveInteger
      );

  const tenorStep = option.isSome(currentLoanOffer.stepSizeTenorMonths)
    ? currentLoanOffer.stepSizeTenorMonths.value
    : unsafePositiveInteger(3);

  const saveSelectedCustomerOffer = useCommand(api.saveSelectedCustomerOffer);
  const formatMessage = useFormatMessage();
  const formatRichMessage = useFormatRichMessage2();
  const formatMoneyAmount = useFormatMoneyAmount(unsafeNonNegativeInteger(0));
  const formatMoneyAmountParts = useFormatMoneyAmountParts(
    unsafeNonNegativeInteger(0)
  );
  const formatCurrency = useFormatCurrency();
  const [isCalculating, setIsCalculating] = useState(false);

  const getGenericLoanOffer = (fullOffer: api.GenericLoanResponseOutput) =>
    pipe(
      fullOffer.genericLoanOffers,
      option.map(offers => offers["NO_SALARY_TRANSFER_OFFER"])
    );

  const genericLoanOffer = getGenericLoanOffer(currentLoanOffer);
  const fetchedGenericLoanOffer = getGenericLoanOffer(props.loanOffer);

  const standardOfferValues = genericLoanOffer;
  const benefitsOfferValues = pipe(
    currentLoanOffer.genericLoanOffers,
    option.map(offers => offers.SALARY_TRANSFER_OFFER)
  );

  const clampAndRoundAmount = makeClampAndRound({
    min: currentLoanOffer.minAmount,
    max: currentLoanOffer.maxAmount,
    step: amountStep,
  });

  const clampAndRoundTenor = makeClampAndRound({
    min: currentLoanOffer.minTenor,
    max: currentLoanOffer.maxTenor,
    step: tenorStep,
  });

  function formatLabelContent(
    currency: Currency,
    message: LocaleKey
  ): Reader<number, LocalizedString> {
    return value =>
      formatMessage(
        message,
        formatMoneyAmount(
          { amount: unsafeNonNegative(value), currency },
          identity
        )
      );
  }

  const updateCustomerOfferNow = (values: ConfiguratorValues) => {
    setCurrentLoanOffer(offer => ({
      ...offer,
      isLoading: true,
    }));

    const updateCustomerOfferValues: ConfiguratorValues = {
      amount: pipe(
        values.amount,
        clampAndRoundAmount,
        NonNegative.decode,
        either.getOrElse(() => currentLoanOffer.minAmount)
      ),
      tenor: pipe(
        values.tenor,
        clampAndRoundTenor,
        PositiveInteger.decode,
        either.getOrElse(() => currentLoanOffer.maxTenor)
      ),
      installmentDay: values.installmentDay,
      salaryTransfer: values.salaryTransfer,
      bankingFeeIncluded: values.bankingFeeIncluded,
    };

    const updateCustomerOfferCallBack = (
      updatedOffer: Option<api.GenericLoanResponseOutput>
    ) => {
      pipe(
        updatedOffer,
        option.fold(
          () =>
            setCurrentLoanOffer(prevOffer => ({
              ...prevOffer,
              isLoading: false,
            })),
          offer =>
            setCurrentLoanOffer({
              ...values,
              ...offer,
              isLoading: false,
            })
        )
      );
    };

    handleUpdateCustomerOffer(updateCustomerOfferValues, ({ genericLoan }) => {
      setIsCalculating(false);
      updateCustomerOfferCallBack(genericLoan);
    });
  };

  const debounceUpdate = useDebounceAction(updateCustomerOfferNow, 1000);

  const onAmountUpdate: Reader<NonNegative, void> = amount => {
    setCurrentLoanOffer(offer => ({
      ...offer,
      amount,
    }));

    setIsCalculating(true);
    updateCustomerOfferNow({
      ...currentLoanOffer,
      amount,
      installmentDay: pipe(
        currentLoanOffer.installmentDay,
        option.getOrElse(() => unsafePositiveInteger(0))
      ),
    });
  };

  const onTenorUpdate: Reader<PositiveInteger, void> = tenor => {
    setCurrentLoanOffer(offer => ({
      ...offer,
      tenor,
    }));

    setIsCalculating(true);
    updateCustomerOfferNow({
      ...currentLoanOffer,
      tenor,
      installmentDay: pipe(
        currentLoanOffer.installmentDay,
        option.getOrElse(() => unsafePositiveInteger(0))
      ),
    });
  };

  const onInstallmentDayUpdate: Reader<
    PositiveInteger,
    void
  > = installmentDay => {
    setCurrentLoanOffer(offer => ({
      ...offer,
      installmentDay: option.some(installmentDay),
    }));

    setIsCalculating(true);
    debounceUpdate({
      ...currentLoanOffer,
      installmentDay,
    });
  };

  const onEntryFeeOptionChange: Reader<Option<boolean>, void> = feeIncluded => {
    setBankingFeeValid(true);
    setCurrentLoanOffer(offer => ({
      ...offer,
      bankingFeeIncluded: feeIncluded,
    }));

    setIsCalculating(true);
    updateCustomerOfferNow({
      ...currentLoanOffer,
      installmentDay: pipe(
        currentLoanOffer.installmentDay,
        option.getOrElse(() => unsafePositiveInteger(0))
      ),
      bankingFeeIncluded: feeIncluded,
    });
  };

  const onCpiInitialState = () => {
    setShowCPIList({
      showCPIList: false,
      salaryTransfer: false,
    });
  };

  const onCpiChange = () => {
    setCurrentLoanOffer(offer => ({
      ...offer,
      isLoading: true,
    }));

    handleUpdateCustomerOffer(
      {
        ...currentLoanOffer,
        installmentDay: pipe(
          currentLoanOffer.installmentDay,
          option.getOrElse(() => unsafePositiveInteger(0))
        ),
        bankingFeeIncluded: currentLoanOffer.bankingFeeIncluded,
      },
      ({ genericLoan, updateOfferStatus }) => {
        setIsCalculating(false);
        pipe(
          genericLoan,
          option.fold(
            () =>
              setCurrentLoanOffer(prevOffer => ({
                ...prevOffer,
                isLoading: false,
              })),
            offer => setCurrentLoanOffer({ ...offer, isLoading: false })
          )
        );
        props.onUpdateCustomerOffer(updateOfferStatus);
      }
    );
  };

  const productType = pipe(
    props.loanOffer.productType,
    option.fold(
      () => "",
      productType => productType
    )
  );

  const isTopUpActive =
    option.isSome(props.authenticationMethod) && productType === "TL";

  const offerLimitsInformation = useOfferLimitsInformation(
    props.loanOffer,
    pipe(
      props.loanOffer.wayOfIdentification,
      option.getOrElse<api.WayOfIdentification>(() => "UNKNOWN")
    ),
    preapprovedLimitState,
    isTopUpActive,
    props.allInternalLoanSelected,
    props.selectedRefinancingCredits
  );

  const hasSelectedRefinancingCredits =
    props.selectedRefinancingCredits.internal.count +
      props.selectedRefinancingCredits.external.count >
    0;

  const [showCPIList, setShowCPIList] = useState({
    showCPIList: false,
    salaryTransfer: false,
  });

  const hasSelectedInsurance = () => {
    const r = pipe(
      currentLoanOffer.insurance,
      option.fold(constFalse, a => a !== "None")
    );
    return r;
  };

  const cpiEligibleByAge = pipe(
    props.loanOffer.cpiEligibleByAge,
    option.fold(constTrue, a => a)
  );
  const onChooseOffer = (
    cbk: TaskEither<unknown, unknown>
  ): TaskEither<unknown, unknown> => {
    const validateForm = enableBankFee50
      ? pipe(
          currentLoanOffer.bankingFee,
          option.fold(constTrue, fee =>
            fee === 0
              ? true
              : pipe(currentLoanOffer.bankingFeeIncluded, option.isSome)
          )
        )
      : true;

    return validateForm
      ? cbk
      : taskEither.leftIO(() => {
          entryFeeProps.issues = option.some(
            fieldIssues.errors([formatMessage("Form.fieldError.required")])
          );

          setBankingFeeValid(false);

          entryFeeRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          forceUpdate();
        });
  };

  const entryFeeProps: FieldProps<Option<boolean>> = {
    label: unsafeLocalizedString(""),
    onBlur: constVoid,
    value: currentLoanOffer.bankingFeeIncluded,
    issues: option.none,
    name: unsafeLocalizedString(""),
    onChange: onEntryFeeOptionChange,
  };

  const benefitsOfferCard = pipe(
    benefitsOfferValues,
    option.fold(constant(<></>), offer => (
      <CustomerOfferCard
        {...offerLimitsInformation}
        title={formatMessage("StandardLoan.CustomerOffer.benefitsOffer.title")}
        recommended
        amount={offer.monthlyPayment}
        tenor={offer.tenor}
        interestRate={offer.interestRateDecimal}
        onViewDetails={() => {
          setOfferDetails(
            option.some({
              genericLoanOffer: offer,
              refinancing:
                props.hasRefinancingCredits && hasSelectedRefinancingCredits,
            })
          );
        }}
        onChooseOffer={onChooseOffer(
          hasSelectedInsurance() || !cpiEligibleByAge
            ? pipe(
                saveSelectedCustomerOffer({
                  salaryTransferOfferKey: "SALARY_TRANSFER_OFFER",
                }),
                taskEither.chain(() => taskEither.fromIO(props.onNext))
              )
            : taskEither.fromIO(() =>
                setShowCPIList({
                  showCPIList: true,
                  salaryTransfer: true,
                })
              )
        )}
        currency={currentLoanOffer.currency}
        showDisclaimer={true}
        insuranceFee={offer.cpimonthlyAmount}
      />
    ))
  );

  const standardOfferCard = pipe(
    standardOfferValues,
    option.fold(constant(<></>), offer => (
      <CustomerOfferCard
        {...offerLimitsInformation}
        title={formatMessage("StandardLoan.CustomerOffer.standardOffer.title")}
        recommended={false}
        amount={offer.monthlyPayment}
        tenor={offer.tenor}
        interestRate={offer.interestRateDecimal}
        onViewDetails={() => {
          setOfferDetails(
            option.some({
              genericLoanOffer: offer,
              refinancing:
                props.hasRefinancingCredits && hasSelectedRefinancingCredits,
            })
          );
        }}
        onChooseOffer={onChooseOffer(
          hasSelectedInsurance() || !cpiEligibleByAge
            ? pipe(
                saveSelectedCustomerOffer({
                  salaryTransferOfferKey: "NO_SALARY_TRANSFER_OFFER",
                }),
                taskEither.chain(() => taskEither.fromIO(props.onNext))
              )
            : taskEither.fromIO(() =>
                setShowCPIList({
                  showCPIList: true,
                  salaryTransfer: false,
                })
              )
        )}
        currency={currentLoanOffer.currency}
        showDisclaimer={false}
        insuranceFee={offer.cpimonthlyAmount}
      />
    ))
  );

  const installmentDayConst = pipe(
    currentLoanOffer.installmentDayList,
    option.fold(
      () => ({ min: 5, max: 25 }),
      installmentDayList => ({
        min: pipe(installmentDayList, nonEmptyArray.head),
        max: pipe(installmentDayList, nonEmptyArray.last),
      })
    )
  );

  const renderOffers = isMobileLayout ? (
    <Stack column units={8}>
      {benefitsOfferCard}
      {standardOfferCard}
    </Stack>
  ) : (
    <ContentRow type="1-1">
      {benefitsOfferCard}
      {standardOfferCard}
    </ContentRow>
  );

  const preApprovedBanner = pipe(
    isTopUpActive,
    boolean.fold(
      () => offerLimitsInformation.preapprovedLimit,
      () => offerLimitsInformation.topUpLimit
    ),
    option.fold(constNull, limit => (
      <Banner
        type="informative"
        title={option.none}
        content={formatMessage(
          "StandardLoan.CustomerOffer.preApprovedBannerMessage",
          formatMoneyAmount(limit, identity)
        )}
        actions={option.none}
        onDismiss={option.none}
      />
    ))
  );

  useEffect(() => {
    pipe(
      loanOfferState,
      foldAPIStatus({
        whenError: constVoid,
        whenLoading: constVoid,
        whenSuccess: data =>
          setCurrentLoanOffer({ ...data.genericLoan, isLoading: false }),
      })
    );
  }, [loanOfferState]);

  const entryFeeRef = useRef<HTMLElement>(null);
  const renderEntryFee = enableBankFee50
    ? pipe(
        currentLoanOffer.bankingFee,
        option.fold(constNull, entryFee =>
          entryFee == 0 ? null : (
            <EntryFeeRadioGroup
              {...entryFeeProps}
              ref={entryFeeRef}
              entryFee={{
                feeIncluded: currentLoanOffer.bankingFeeIncluded,
                amount: entryFee,
              }}
              disabled={isCalculating}
              issues={
                bankingFeeValid
                  ? option.none
                  : option.some(
                      fieldIssues.errors([
                        formatMessage("Form.fieldError.required"),
                      ])
                    )
              }
            />
          )
        )
      )
    : null;

  const [showLoading, setShowLoading] = useState(false);

  return showLoading ? (
    <Box hAlignContent="center">
      <Loader
        ariaLabelMessage={formatMessage(
          "generic.loading.alt" as RuntimeLocaleKey
        )}
      />
    </Box>
  ) : (
    <Stack column grow shrink units={isMobileLayout ? 4 : 8}>
      <Heading size="small" weight="medium">
        {formatMessage("StandardLoan.CustomerOffer.LoanConfiguration.title")}
      </Heading>

      {pipe(
        props.loanOffer.eligibleForPreapproved,
        option.fold(
          () => preApprovedBanner,
          eligibleForPreapproved =>
            eligibleForPreapproved === 1 && preApprovedBanner
        )
      )}
      <span id={"minusAriaLabel"} className={"sr-only"}>
        {formatMessage("Slider.Minus.AriaLabel")}
      </span>
      <span id={"plusAriaLabel"} className={"sr-only"}>
        {formatMessage("Slider.Plus.AriaLabel")}
      </span>
      <InputSliderField
        value={props.loanOffer.amount}
        onChange={val =>
          pipe(isNaN(val) ? 0 : val, flow(unsafeNonNegative, onAmountUpdate))
        }
        min={props.loanOffer.minAmount}
        max={props.loanOffer.maxAmount}
        step={amountStep}
        syncInputWithSteps
        label={
          hasSelectedRefinancingCredits
            ? formatMessage("StandardLoan.CustomerOffer.amountLabelRefinancing")
            : formatMessage("StandardLoan.CustomerOffer.amountLabel")
        }
        minLabelContent={formatLabelContent(
          props.loanOffer.currency,
          "StandardLoan.CustomerOffer.minAmountLabel"
        )}
        minAriaLabel={formatLabelContent(
          props.loanOffer.currency,
          "StandardLoan.CustomerOffer.minAmountAriaLabel"
        )}
        maxLabelContent={formatLabelContent(
          props.loanOffer.currency,
          "StandardLoan.CustomerOffer.maxAmountLabel"
        )}
        maxAriaLabel={formatLabelContent(
          props.loanOffer.currency,
          "StandardLoan.CustomerOffer.maxAmountAriaLabel"
        )}
        minusAriaLabelledBy={unsafeLocalizedString("minusAriaLabel")}
        plusAriaLabelledBy={unsafeLocalizedString("plusAriaLabel")}
        name="amount"
        issues={option.none}
        onBlur={constVoid}
        rightContent={formatCurrency(props.loanOffer.currency)}
        disabled={isCalculating}
        locale="sk"
      />
      <LimitWarning
        {...offerLimitsInformation}
        currency={currentLoanOffer.currency}
        loanOffer={props.loanOffer}
        onExit={props.onExit}
      />
      <span id={"minusAriaLabel"} className={"sr-only"}>
        {formatMessage("Slider.Minus.AriaLabel")}
      </span>
      <span id={"plusAriaLabel"} className={"sr-only"}>
        {formatMessage("Slider.Plus.AriaLabel")}
      </span>
      <InputSliderField
        value={currentLoanOffer.tenor}
        onChange={flow(
          PositiveInteger.decode,
          either.getOrElse(() => unsafePositiveInteger(1)),
          onTenorUpdate
        )}
        min={currentLoanOffer.minTenor}
        max={currentLoanOffer.maxTenor}
        step={tenorStep}
        syncInputWithSteps
        minLabelContent={() =>
          formatMessage("StandardLoan.CustomerOffer.minTenorLabel", {
            months: currentLoanOffer.minTenor,
          })
        }
        minAriaLabel={() =>
          formatMessage("StandardLoan.CustomerOffer.minTenorAriaLabel", {
            months: currentLoanOffer.minTenor,
          })
        }
        maxLabelContent={() =>
          formatMessage("StandardLoan.CustomerOffer.maxTenorLabel", {
            months: currentLoanOffer.maxTenor,
          })
        }
        maxAriaLabel={() =>
          formatMessage("StandardLoan.CustomerOffer.maxTenorAriaLabel", {
            months: currentLoanOffer.maxTenor,
          })
        }
        minusAriaLabelledBy={unsafeLocalizedString("minusAriaLabel")}
        plusAriaLabelledBy={unsafeLocalizedString("plusAriaLabel")}
        name="currentTenor"
        issues={option.none}
        label={formatMessage("StandardLoan.CustomerOffer.tenorLabel")}
        onBlur={constVoid}
        rightContent={formatMessage("StandardLoan.CustomerOffer.tenorUnits")}
        disabled={isCalculating}
      />
      <>
        {pipe(
          pipe(
            props.loanOffer.displayMinTenorInfoLabel,
            option.fold(
              () => false,
              displayMinTenorInfoLabel => displayMinTenorInfoLabel
            )
          ),
          boolean.fold(constNull, () =>
            pipe(
              offerLimitsInformation.moneyAmountLimit,
              option.fold(constNull, moneyAmountLimit =>
                pipe(
                  props.loanOffer.minTenorForPreapproved,
                  option.fold(constNull, minTenorForPreapproved => {
                    return (
                      <InlineMessage
                        type="warning"
                        message={formatMessage(
                          "StandardLoan.CustomerOffer.info.preapprovedLimit.minTenor",
                          {
                            ...formatMoneyAmountParts({
                              amount: moneyAmountLimit.amount,
                              currency: moneyAmountLimit.currency,
                            }),
                            minTenorForPreapproved: minTenorForPreapproved,
                          }
                        )}
                        size="medium"
                      />
                    );
                  })
                )
              )
            )
          )
        )}
      </>
      <Box column>
        <Box vAlignContent="center">
          <Body size={"medium"} weight={"bold"}>
            {formatMessage("StandardLoan.CustomerOffer.text1")}
          </Body>
          <Space units={4} />
          <Button
            type="button"
            label={formatMessage("StandardLoan.CustomerOffer.text1.moreInfo")}
            variant="text"
            size="small"
            action={() => {
              setMoreInfo(true);
            }}
          />
          {moreInfo && (
            <Dialog
              variant="center"
              size="medium"
              title={formatMessage(
                "StandardLoan.CustomerOffer.text1.moreInfo.title"
              )}
              onDismiss={option.some(() => setMoreInfo(false))}
              actions={[]}
              icon={InfoIcon}
            >
              <Box
                column
                shrink
                style={{ marginLeft: "6px", marginRight: "6px" }}
              >
                <Body size={"medium"} weight={"regular"}>
                  {formatRichMessage(
                    "StandardLoan.CustomerOffer.text1.moreInfo.body"
                  )}
                </Body>
              </Box>
            </Dialog>
          )}
        </Box>

        <Space units={4} />

        <Body size={"medium"} weight={"bold"}>
          {formatMessage("StandardLoan.CustomerOffer.text2")}
        </Body>
      </Box>
      <span id={"minusTenorAriaLabel"} className={"sr-only"}>
        {formatMessage("Tenor.Minus.AriaLabel")}
      </span>
      <span id={"plusTenorAriaLabel"} className={"sr-only"}>
        {formatMessage("Tenor.Plus.AriaLabel")}
      </span>
      <NumberInputStepperField
        onBlur={constVoid}
        name="dayOfInstallment"
        label={formatMessage(
          "StandardLoan.CustomerOffer.dayOfInstallmentLabel",
          {
            min: installmentDayConst.min,
            max: installmentDayConst.max,
          }
        )}
        value={currentLoanOffer.installmentDay}
        onChange={flow(
          option.map(PositiveInteger.decode),
          option.fold(constVoid, either.fold(constVoid, onInstallmentDayUpdate))
        )}
        min={installmentDayConst.min}
        max={installmentDayConst.max}
        minusAriaLabelledBy={unsafeLocalizedString("minusTenorAriaLabel")}
        plusAriaLabelledBy={unsafeLocalizedString("plusTenorAriaLabel")}
        issues={pipe(
          currentLoanOffer.installmentDayList,
          option.chain(installmentDayList =>
            pipe(
              currentLoanOffer.installmentDay,
              option.chain(installmentDay =>
                pipe(
                  installmentDayList,
                  array.findFirst(e => e === installmentDay),
                  option.fold(
                    () =>
                      option.some(
                        fieldIssues.errors([
                          formatMessage(
                            "StandardLoan.CustomerOffer.dayOfInstallment.validationError",
                            {
                              installmentDay,
                            }
                          ),
                        ])
                      ),
                    () => option.none
                  )
                )
              )
            )
          )
        )}
      />
      <>
        {pipe(
          props.cpiPackageList,
          option.fold(constNull, cpiPackageList => (
            <CPIDialog
              currentOfferInstallment={{
                amount: currentLoanOffer.installment,
                currency: currentLoanOffer.currency,
              }}
              restoredAdditionalQuestions={props.restoredAdditionalQuestions}
              onCpiChange={onCpiChange}
              onCpiInitialState={onCpiInitialState}
              cpiPackageList={cpiPackageList}
              noSelection={showCPIList.showCPIList}
              onNoCPIWanted={pipe(
                taskEither.fromIO(() => setShowLoading(true)),
                taskEither.chain(() =>
                  saveSelectedCustomerOffer({
                    salaryTransferOfferKey: showCPIList.salaryTransfer
                      ? "SALARY_TRANSFER_OFFER"
                      : "NO_SALARY_TRANSFER_OFFER",
                  })
                ),
                taskEither.chain(() => taskEither.fromIO(props.onNext))
              )}
            />
          ))
        )}
      </>
      {renderEntryFee}
      <Divider width="100%" />
      <>
        {pipe(
          fetchedGenericLoanOffer,
          option.alt(() => genericLoanOffer),
          option.fold(constNull, loanOffer => (
            <YourOffer
              currentLoanOffer={currentLoanOffer}
              genericLoanOffer={loanOffer}
            />
          ))
        )}
      </>
      <Heading size="medium" weight="medium">
        {formatMessage("StandardLoan.CustomerOffer.selectOption.not.transfer")}
      </Heading>

      {renderOffers}
      <>
        {pipe(
          offerDetails,
          option.fold(constNull, details => (
            <CustomerOfferDetailsDialog
              onDismiss={() => setOfferDetails(option.none)}
              customerLoanOffer={currentLoanOffer}
              offerDetails={details}
            />
          ))
        )}
      </>
    </Stack>
  );
}
